import { Row } from '@components/Layout/Row';
import { SummaryContext } from '@components/SummarySelection/context';
import { SummaryCard } from '@components/SummarySelection/SummaryCard';
import { SummaryItemHolder } from '@components/SummarySelection/SummaryItemHolder';
import Divider from '@material-ui/core/Divider';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import { PackageType } from '@model/Package';
import { SkuType } from '@model/Sku';
import { createMemo } from '@packages/react';
import { IF } from '@packages/react/lib/if';
import React, { useCallback, useContext } from 'react';
import DRCardWrapper from '../DRCardWrapper';
import { SummaryPackagePlaceholder } from './SummaryPackagePlaceholder';

export const SummarySkus = createMemo<{ showPrices?: boolean; isDR?: boolean }>(
    ({ showPrices, isDR }) => {
        const [state, actions] = useContext(SummaryContext);
        const isItemsSelected = state.get('products').length > 0 || state.get('skus').length > 0;
        let selectedSkus = state.get('skus_ids') || [];

        if (isDR) {
            selectedSkus = (state.get('skus') || []).map(({ id }) => id);
        }
        const categoryId = state.get('category_id');

        const handlePackageSelect = useCallback(
            (pkg: PackageType) => {
                actions.toggleSku(pkg);
            },
            [selectedSkus.join('_')],
        );

        let skus$: any = null;

        if (state.get('loading')) {
            skus$ = (
                <>
                    <SummaryItemHolder>
                        <SummaryPackagePlaceholder />
                    </SummaryItemHolder>
                    <Hidden xsDown>
                        <SummaryItemHolder>
                            <SummaryPackagePlaceholder />
                        </SummaryItemHolder>
                    </Hidden>
                    <Hidden smDown>
                        <SummaryItemHolder>
                            <SummaryPackagePlaceholder />
                        </SummaryItemHolder>
                    </Hidden>
                    <Hidden mdDown>
                        <SummaryItemHolder>
                            <SummaryPackagePlaceholder />
                        </SummaryItemHolder>
                    </Hidden>
                </>
            );
        } else {
            if (!state.getIn(['errors', 'skus'])) {
                if (state.hasIn(['rawData', 'skus', 'items'])) {
                    const skusList = state.getIn(['rawData', 'skus', 'items']) as SkuType[];
                    const skusCollection = Object.values(
                        skusList.reduce((acc: any, sku: SkuType) => {
                            const subCategory = sku.categories_subcategories.find(
                                ({ category_id }) => category_id === categoryId,
                            ) || { id: 0, name: 'General' };

                            return {
                                ...(acc as any),
                                [subCategory.id]: {
                                    name: subCategory.name,
                                    items: [...(acc[subCategory.id] ? acc[subCategory.id].items : []), sku] as any,
                                },
                            };
                        }, {}),
                    );

                    skus$ = skusCollection.map<any>(
                        ({ name, items }: any, idx): any =>
                            (
                                <Row padding={'16px 0 0'} key={idx}>
                                    <Row padding={isDR ? '0' : '0 24px'}>
                                        <Typography>{name}</Typography>
                                    </Row>
                                    <Row padding={isDR ? '0 0 16px' : '0 16px 16px'}>
                                        {items.map((item) => {
                                            const seo = findSeoImageTitleByCategoryId(item, categoryId);
                                            item.name = seo?.product_title || item.name;
                                            item.preview_photo = seo?.product_image || item.preview_photo;

                                            const child = (
                                                <SummaryCard
                                                    selected={selectedSkus.includes(item.id)}
                                                    variant={'sku'}
                                                    data={item}
                                                    onClick={handlePackageSelect}
                                                    showPrices={showPrices}
                                                    isDR={isDR}
                                                    key={item.id}
                                                />
                                            );
                                            return isDR ? (
                                                <DRCardWrapper isItemSelected={isItemsSelected}>
                                                    {' '}
                                                    {child}{' '}
                                                </DRCardWrapper>
                                            ) : (
                                                <SummaryItemHolder key={item.id}>{child}</SummaryItemHolder>
                                            );
                                        })}
                                    </Row>
                                    {IF(idx < skusCollection.length - 1, <Divider style={{ width: '100%' }} />)}
                                </Row>
                            ) as any,
                    );
                }
            } else {
                skus$ = "Extra Options couldn't be loaded!";
            }
        }

        return <Row>{skus$}</Row>;
    },
    {
        displayName: 'SummarySkus',
    },
);

export const findSeoImageTitleByCategoryId = (product, categoryId) => {
    return product?.categories_subcategories?.find((subcategory) => subcategory?.category_id === categoryId) || null;
};
